import { memo } from "react";
import type { ExecutiveSnapshot, CampusForecast, FallbackTier } from "@kmitl/shared";
import { ForecastSparkline } from "./ForecastSparkline";
import { PanelHeader } from "./PanelHeader";

interface Props {
  data: ExecutiveSnapshot | null;
  loading: boolean;
  forecast?: CampusForecast | null;
  ageMinutes?: number | null;
  fallbackTier?: FallbackTier;
}

function rankTrend(prev: number, curr: number): { arrow: string; color: string } {
  if (curr < prev) return { arrow: "↑", color: "var(--good)" }; // lower rank is better
  if (curr > prev) return { arrow: "↓", color: "var(--bad)" };
  return { arrow: "→", color: "var(--text-3)" };
}

function fmt(n: number): string {
  return n.toLocaleString("en-US");
}

function statusColor(s: ExecutiveSnapshot["initiatives"][number]["status"]): string {
  switch (s) {
    case "on-track":
      return "var(--good)";
    case "at-risk":
      return "var(--warn)";
    case "delayed":
      return "var(--bad)";
    case "completed":
      return "var(--data)";
  }
}

/**
 * SourcePill — small status pill rendered next to a section eyebrow.
 *
 * Three tones map to dashboard colour discipline:
 *   live     → --good   (data is the real upstream signal, this minute)
 *   fallback → --warn   (real path failed; using a second-best source)
 *   pending  → --text-3 (nothing live; modeled or not generated yet)
 *
 * Used for TimeFM (timefm / open-meteo-fallback / scenario) and AlphaEarth
 * (manifest available / not generated). Replaces ad-hoc captions so the
 * data-state read is consistent across panels.
 */
function SourcePill({
  label,
  tone,
  tooltip,
}: {
  label: string;
  tone: "live" | "fallback" | "pending";
  tooltip?: string;
}) {
  const TONE_COLOR: Record<typeof tone, string> = {
    live: "var(--good)",
    fallback: "var(--warn)",
    pending: "var(--text-3)",
  };
  return (
    <span
      className="mono"
      title={tooltip ?? label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: "0.66rem",
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        padding: "2px 6px",
        borderRadius: 3,
        color: TONE_COLOR[tone],
        border: `1px solid ${TONE_COLOR[tone]}`,
        background: "rgba(255,255,255,0.02)",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: TONE_COLOR[tone],
        boxShadow: tone === "live" ? `0 0 4px ${TONE_COLOR[tone]}` : "none",
      }} />
      {label}
    </span>
  );
}

function activityColor(level: string | null): string {
  if (level === "high") return "var(--bad)";
  if (level === "moderate") return "var(--warn)";
  if (level === "low") return "var(--data)";
  return "var(--good)";
}

export const ExecutiveBrief = memo(function ExecutiveBrief({ data, loading, forecast, ageMinutes, fallbackTier }: Props) {
  if (loading && !data) {
    return (
      <div className="exec-brief">
        <PanelHeader title="EXECUTIVE BRIEF" ageMinutes={ageMinutes} fallbackTier={fallbackTier} />
        <div className="skeleton" style={{ height: 10, marginTop: 8 }} />
        <div className="skeleton" style={{ height: 10, width: "80%", marginTop: 6 }} />
        <div className="skeleton" style={{ height: 10, width: "60%", marginTop: 6 }} />
      </div>
    );
  }

  const d = data;
  if (!d) return null;

  const updatedHm = (() => {
    try {
      const t = new Date(d.updatedAt);
      if (Number.isNaN(t.getTime())) return null;
      return new Intl.DateTimeFormat("en-GB", {
        timeZone: "Asia/Bangkok",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(t);
    } catch {
      return null;
    }
  })();

  return (
    <div className="exec-brief">
      <PanelHeader
        title="EXECUTIVE BRIEF"
        ageMinutes={ageMinutes}
        fallbackTier={fallbackTier}
        source="bma·qs·the"
        actions={updatedHm ? <span className="mono caption" style={{ color: "var(--text-3)" }}>upd {updatedHm}</span> : undefined}
      />
      {/* ── Rankings ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">Rankings</span>
          <span className="mono caption">
            {d.rankings[0]?.year} cycle
          </span>
        </header>
        <div className="exec-rank-grid">
          {d.rankings.map((r) => {
            const t = rankTrend(r.previousRank, r.rank);
            const displayRank = r.rankBand ?? `#${r.rank.toLocaleString("en-US")}`;
            return (
              <div key={r.system} className="exec-rank">
                <div className="exec-rank-label">{r.label}</div>
                <div className="exec-rank-num" style={{ color: t.color }}>
                  {t.arrow} {displayRank}
                </div>
                <div className="exec-rank-sub mono">{r.system === "nature-index" ? `Share ${r.rank === 7 ? "0.38" : r.rank}` : `of ${r.total.toLocaleString("en-US")}`}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Enrollment ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">Enrollment</span>
          <span className="mono caption">{fmt(d.enrollment.total)} total</span>
        </header>
        <div className="exec-kv-grid">
          <div className="exec-kv">
            <div className="num">{fmt(d.enrollment.undergraduate)}</div>
            <div className="lbl">UNDERGRAD</div>
          </div>
          <div className="exec-kv">
            <div className="num">{fmt(d.enrollment.graduate)}</div>
            <div className="lbl">GRADUATE</div>
          </div>
          <div className="exec-kv">
            <div className="num">{fmt(d.enrollment.international)}</div>
            <div className="lbl">INTERNATIONAL</div>
          </div>
          <div className="exec-kv">
            <div className="num">{d.enrollment.internationalPct}%</div>
            <div className="lbl">INTL SHARE</div>
          </div>
        </div>
        <div className="exec-foot mono">
          {d.enrollment.faculties} schools &amp; colleges · ratio {d.enrollment.studentFacultyRatio}
        </div>
      </section>

      {/* ── Research ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">Research</span>
          <span className="mono caption">2024</span>
        </header>
        <div className="exec-kv-grid">
          <div className="exec-kv">
            <div className="num">{fmt(d.research.publications2024)}</div>
            <div className="lbl">PUBLICATIONS</div>
          </div>
          <div className="exec-kv">
            <div className="num">{fmt(d.research.citations2024)}</div>
            <div className="lbl">CITATIONS</div>
          </div>
          <div className="exec-kv">
            <div className="num">{d.research.hIndex}</div>
            <div className="lbl">H-INDEX</div>
          </div>
          <div className="exec-kv">
            <div className="num">{d.research.patentsFiled != null ? d.research.patentsFiled : "—"}</div>
            <div className="lbl">PATENTS</div>
          </div>
        </div>
        <div className="exec-foot mono">Top: {d.research.topFields.join(" · ")}</div>
      </section>

      {/* ── Finance ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">Finance</span>
          <span className="mono caption" style={{ color: "var(--ink-low)" }}>SCENARIO · PUBLIC DISCLOSURES</span>
        </header>
        <div className="exec-kv-grid">
          <div className="exec-kv">
            <div className="num">{d.finance.annualBudgetBThb != null ? d.finance.annualBudgetBThb.toFixed(1) : "—"}</div>
            <div className="lbl">BUDGET (B THB)</div>
          </div>
          <div className="exec-kv">
            <div className="num">{d.finance.researchGrantsMThb != null ? (d.finance.researchGrantsMThb / 1000).toFixed(1) : "—"}</div>
            <div className="lbl">GRANTS (B THB)</div>
          </div>
          <div className="exec-kv">
            <div className="num">{d.finance.endowmentBThb != null ? d.finance.endowmentBThb.toFixed(1) : "—"}</div>
            <div className="lbl">ENDOWMENT (B THB)</div>
          </div>
        </div>
        {d.finance.revenueBreakdown && (
          <div className="exec-revenue-bars">
            <div className="exec-revenue-label mono">REVENUE SOURCES</div>
            {[
              { label: "GOVT ALLOCATION", pct: d.finance.revenueBreakdown.governmentPct, color: "var(--data)" },
              { label: "AUXILIARY & SERVICES", pct: d.finance.revenueBreakdown.commercialPct, color: "var(--accent)" },
              { label: "TUITION & FEES",  pct: d.finance.revenueBreakdown.tuitionPct,   color: "var(--good)" },
              { label: "RESEARCH GRANTS", pct: d.finance.revenueBreakdown.grantsPct,    color: "var(--warn)" },
              { label: "OTHER",           pct: d.finance.revenueBreakdown.otherPct,     color: "var(--ink-low)" },
            ].map(({ label, pct, color }) => (
              <div key={label} className="exec-revenue-row">
                <span className="exec-revenue-name mono">{label}</span>
                <div className="exec-revenue-track">
                  <div className="exec-revenue-fill" style={{ width: `${pct}%`, background: color }} />
                </div>
                <span className="exec-revenue-pct mono">{pct}%</span>
              </div>
            ))}
          </div>
        )}
        <div className="exec-foot mono" style={{ color: "var(--ink-low)" }}>
          {d.finance.note}
        </div>
      </section>

      {/* ── Smart Campus ROI ── */}
      {d.smartCampus && (
        <section className="exec-section">
          <header className="exec-h">
            <span className="eyebrow mono" style={{ color: "var(--brand)" }}>Smart Campus</span>
            <span className="mono caption" style={{ color: "var(--ink-low)" }}>SCENARIO · ENERGY ROI</span>
          </header>
          <div className="exec-kv-grid">
            <div className="exec-kv">
              <div className="num">฿{fmt(d.smartCampus.annualEnergyCostTHBM)}M</div>
              <div className="lbl">electricity spend / yr</div>
            </div>
            <div className="exec-kv">
              <div className="num">{fmt(d.smartCampus.carbonTonnesPerYear)}</div>
              <div className="lbl">tCO₂ / yr · scope 2</div>
            </div>
            <div className="exec-kv" style={{ borderColor: "var(--brand)" }}>
              <div className="num" style={{ color: "var(--brand)" }}>
                ฿{fmt(d.smartCampus.estimatedSavingsTHBMLow)}–{fmt(d.smartCampus.estimatedSavingsTHBMHigh)}M
              </div>
              <div className="lbl" style={{ color: "var(--brand)" }}>
                savings potential ({d.smartCampus.estimatedSavingsPctLow}–{d.smartCampus.estimatedSavingsPctHigh}%)
              </div>
            </div>
            <div className="exec-kv">
              <div className="num">{fmt(d.smartCampus.energyIntensityKwhPerSqm)}</div>
              <div className="lbl">kWh / m² intensity</div>
            </div>
          </div>

          {/* Solar target progress */}
          <div className="exec-revenue-bars">
            <div className="exec-revenue-row">
              <span className="exec-revenue-name mono">SOLAR TARGET</span>
              <div className="exec-revenue-track">
                <div
                  className="exec-revenue-fill"
                  style={{
                    width: `${Math.min(
                      100,
                      d.smartCampus.solarTargetKwp > 0
                        ? (d.smartCampus.solarInstalledKwp / d.smartCampus.solarTargetKwp) * 100
                        : 0,
                    )}%`,
                    background: "var(--brand)",
                  }}
                />
              </div>
              <span className="exec-revenue-pct mono">
                {fmt(d.smartCampus.solarInstalledKwp)} / {fmt(d.smartCampus.solarTargetKwp)}
              </span>
            </div>
            <div className="exec-revenue-row">
              <span className="exec-revenue-name mono">METERING</span>
              <div className="exec-revenue-track">
                <div
                  className="exec-revenue-fill"
                  style={{ width: `${d.smartCampus.smartMeterCoveragePct}%`, background: "var(--accent)" }}
                />
              </div>
              <span className="exec-revenue-pct mono">{d.smartCampus.smartMeterCoveragePct} → 100%</span>
            </div>
          </div>

          <div className="exec-foot mono" style={{ color: "var(--ink-low)" }}>
            {fmt(d.smartCampus.annualEnergyMwh)} MWh/yr · {fmt(d.smartCampus.peaContractedKva)} kVA contracted · this dashboard IS the metering roadmap.
          </div>
        </section>
      )}

      {/* ── Strategic initiatives ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">Initiatives</span>
          <span className="mono caption">{d.initiatives.length} active</span>
        </header>
        <ul className="exec-init-list">
          {d.initiatives.map((i) => (
            <li key={i.id} className="exec-init">
              <div className="exec-init-top">
                <span className="exec-init-name">{i.name}</span>
                <span className="exec-init-status mono" style={{ color: statusColor(i.status) }}>
                  {i.status.toUpperCase()}
                </span>
              </div>
              <div className="exec-init-bar">
                <span
                  className="exec-init-fill"
                  style={{
                    width: `${i.progressPct}%`,
                    background: statusColor(i.status),
                  }}
                />
              </div>
              <div className="exec-init-foot mono">
                {i.progressPct}% · {i.owner} · {i.deadline}
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* ── AlphaEarth campus terrain ── */}
      <section className="exec-section">
        <header className="exec-h">
          <span className="eyebrow mono">αEarth · Campus Terrain</span>
          {/* Source-state pill — green if a manifest exists, amber if not. */}
          <SourcePill
            label={
              d.campusTerrain.available
                ? `AlphaEarth · ${d.campusTerrain.lastUpdated?.slice(0, 10) ?? "snapshot"}`
                : "AlphaEarth · not generated"
            }
            tone={d.campusTerrain.available ? "live" : "pending"}
            tooltip={
              d.campusTerrain.available
                ? "Google Satellite Embedding V1 — last manifest generated on the date shown."
                : "Run scripts/alphaearth-extract.py to populate the campus-terrain manifest."
            }
          />
        </header>
        {d.campusTerrain.available ? (
          <>
            <div className="exec-kv-grid">
              <div className="exec-kv">
                <div className="num" style={{ color: "var(--good)" }}>{d.campusTerrain.canopyPct ?? "—"}%</div>
                <div className="lbl">CANOPY</div>
              </div>
              <div className="exec-kv">
                <div className="num" style={{ color: "var(--warn)" }}>{d.campusTerrain.builtUpPct ?? "—"}%</div>
                <div className="lbl">BUILT-UP</div>
              </div>
              <div className="exec-kv">
                <div className="num">{d.campusTerrain.greennessMean?.toFixed(2) ?? "—"}</div>
                <div className="lbl">GREENNESS</div>
              </div>
              <div className="exec-kv">
                <div className="num" style={{ color: activityColor(d.campusTerrain.constructionActivity) }}>
                  {d.campusTerrain.constructionActivity?.toUpperCase() ?? "—"}
                </div>
                <div className="lbl">CONSTRUCTION</div>
              </div>
            </div>
            <div className="exec-foot mono">Source: Google Satellite Embedding V1 · annual</div>
          </>
        ) : (
          <div className="exec-foot mono" style={{ color: "var(--text-3)" }}>
            Run <code>python scripts/alphaearth-extract.py</code> to generate campus terrain intelligence.
          </div>
        )}
      </section>

      {/* ── TimeFM 24h forecasts ── */}
      {forecast && (
        <section className="exec-section">
          <header className="exec-h">
            <span className="eyebrow mono">TimeFM · 24h Outlook</span>
            {/* Three-state model pill: live TimeFM / Open-Meteo fallback /
                scenario. Reads forecast.aqi.model which is the upstream
                signal from the timeFM adapter. */}
            <SourcePill
              label={
                forecast.aqi.model === "timefm"
                  ? "TimeFM (live)"
                  : forecast.aqi.model === "open-meteo"
                  ? "Open-Meteo fallback"
                  : "Scenario (modeled)"
              }
              tone={
                forecast.aqi.model === "timefm"
                  ? "live"
                  : forecast.aqi.model === "open-meteo"
                  ? "fallback"
                  : "pending"
              }
              tooltip={
                forecast.aqi.model === "timefm"
                  ? "TimeFM inference returned a 24h forecast — live model output."
                  : forecast.aqi.model === "open-meteo"
                  ? "TimeFM unavailable; falling back to Open-Meteo direct forecast."
                  : "Open-Meteo also unavailable; rendering a Bangkok rainy-season scenario template."
              }
            />
          </header>
          <div className="exec-forecast-grid">
            <div className="exec-forecast-item">
              <div className="lbl mono">AQI FORECAST</div>
              <ForecastSparkline series={forecast.aqi} height={44} showLabels />
            </div>
            <div className="exec-forecast-item">
              <div className="lbl mono">ENERGY DRAW</div>
              <ForecastSparkline series={forecast.energy} height={44} accentColor="var(--warn)" showLabels />
            </div>
          </div>
        </section>
      )}
    </div>
  );
});
